/**
 * Centralized Site Metadata Configuration
 *
 * This module provides utilities for generating dynamic metadata (titles, descriptions, keywords)
 * for all sites in the Downkount network. It handles:
 * - Default generic sports site metadata
 * - League-specific metadata (e.g., "NFL Time")
 * - Team-specific metadata (e.g., "Lakers Time")
 * - Sport-specific action words (tip-off, kick-off, first-pitch, puck-drop)
 *
 * @module config/siteMetadata
 */

/**
 * Sport-specific action words for different sports
 * Used in keywords like "tip-off time" or "kick-off time"
 */
const SPORT_ACTION_WORDS = {
  // Basketball
  basketball: 'tip-off',
  'college basketball': 'tip-off',

  // Football (American & Soccer)
  football: 'kick-off',
  'american football': 'kick-off',
  soccer: 'kick-off',

  // Baseball
  baseball: 'first-pitch',

  // Hockey
  hockey: 'puck-drop',
  'ice hockey': 'puck-drop',

  // Default for all other sports
  default: 'start',
};

/**
 * League to sport type mapping
 * Used to determine the correct action word for league-specific sites
 */
const LEAGUE_SPORT_MAP = {
  // Basketball
  NBA: 'basketball',
  WNBA: 'basketball',
  NCAAM: 'basketball',
  NCAAW: 'basketball',

  // Football
  NFL: 'football',
  NCAAF: 'football',
  MLS: 'soccer',

  // Baseball
  MLB: 'baseball',
  'NCAA Baseball': 'baseball',

  // Hockey
  NHL: 'hockey',
  'NCAA Hockey': 'hockey',
};

/**
 * Get the sport-specific action word (e.g., "tip-off", "kick-off")
 * @param {string} sportType - Sport type from configuration (e.g., "basketball", "football")
 * @param {string} leagueName - League name (e.g., "NBA", "NFL") - optional, used as fallback
 * @returns {string} Action word (e.g., "tip-off", "kick-off", "first-pitch", "puck-drop", "start")
 */
export const getSportActionWord = (sportType, leagueName = null) => {
  // Try sport type first
  if (sportType) {
    const normalizedSport = sportType.toLowerCase().trim();
    if (SPORT_ACTION_WORDS[normalizedSport]) {
      return SPORT_ACTION_WORDS[normalizedSport];
    }
  }

  // Try league name as fallback
  if (leagueName) {
    const normalizedLeague = leagueName.toUpperCase().trim();
    const mappedSport = LEAGUE_SPORT_MAP[normalizedLeague];
    if (mappedSport && SPORT_ACTION_WORDS[mappedSport]) {
      return SPORT_ACTION_WORDS[mappedSport];
    }
  }

  // Default
  return SPORT_ACTION_WORDS.default;
};

/**
 * Generate keywords for site metadata
 * @param {Object} config - Configuration object
 * @param {string} config.siteName - Site name (e.g., "Lakers Time", "NFL Time", "Downkount")
 * @param {string} config.sportType - Sport type (e.g., "basketball", "football")
 * @param {string} config.sportLevel - Sport level (e.g., "Professional", "College")
 * @param {string} config.leagueName - League name (e.g., "NBA", "NFL")
 * @param {string} config.teamName - Team name (e.g., "Los Angeles Lakers")
 * @param {boolean} config.isOneTeam - Whether this is a single-team site
 * @param {boolean} config.isOneLeague - Whether this is a single-league site
 * @returns {string} Comma-separated keywords
 */
export const generateKeywords = ({
  sportType = '',
  leagueName = '',
  teamName = '',
  isOneTeam = false,
  isOneLeague = false,
}) => {
  const keywords = [];

  // Get sport-specific action word
  const actionWord = getSportActionWord(sportType, leagueName);

  // Team-specific site (e.g., "Lakers Time")
  if (isOneTeam && teamName) {
    keywords.push(
      teamName,
      'events countdown',
      'all events',
      `${actionWord} time`,
      'game',
      'television channel',
      'information'
    );
  }
  // League-specific site (e.g., "NFL Time")
  else if (isOneLeague && leagueName) {
    keywords.push(
      leagueName,
      'events countdown',
      'all events',
      `${actionWord} time`,
      'game',
      'television channel',
      'information'
    );
  }
  // Generic sports site (e.g., "Downkount")
  else {
    keywords.push(
      'sports events countdown',
      'events schedule',
      'all sports leagues',
      'all teams',
      'all sports',
      'game information'
    );
  }

  return keywords.join(', ');
};

/**
 * Generate site title for browser tab and PWA
 * @param {Object} config - Configuration object
 * @param {string} config.siteName - Site name from API
 * @param {string} config.origin - Window origin (e.g., "https://lakerstime.com")
 * @returns {string} Full site title (e.g., "Lakers Time - https://lakerstime.com")
 */
export const generateSiteTitle = ({ siteName = '', origin = '' }) => {
  if (!siteName) return '';
  return `${siteName} - ${origin}`;
};

/**
 * Generate enhanced description for SEO
 * @param {Object} config - Configuration object
 * @param {string} config.siteName - Site name
 * @param {string} config.siteDescription - Site description from API
 * @param {string} config.sportType - Sport type
 * @param {string} config.sportLevel - Sport level
 * @param {string} config.teamName - Team name (for team-specific sites)
 * @param {string} config.leagueName - League name (for league-specific sites)
 * @param {boolean} config.isOneTeam - Whether this is a single-team site
 * @param {boolean} config.isOneLeague - Whether this is a single-league site
 * @returns {string} Enhanced description
 */
export const generateEnhancedDescription = ({
  siteName = '',
  siteDescription = 'The Countdown Continues...',
  sportType = '',
  sportLevel = '',
  teamName = '',
  leagueName = '',
  isOneTeam = false,
  isOneLeague = false,
}) => {
  // Team-specific site
  if (isOneTeam && teamName && sportType) {
    const actionWord = getSportActionWord(sportType, leagueName);
    return `${teamName} countdown and schedule tracker. Never miss a game with live countdowns, ${actionWord} times, TV channels, and game information. ${siteDescription}`;
  }

  // League-specific site
  if (isOneLeague && leagueName && sportType && sportLevel) {
    const actionWord = getSportActionWord(sportType, leagueName);
    return `${siteName} - ${sportLevel} ${sportType} game schedule and countdown tracker. Track ${actionWord} times, TV channels, and game information. ${siteDescription}`;
  }

  // Generic site with sport info
  if (siteName && sportType && sportLevel) {
    return `${siteName} - ${sportLevel} ${sportType} game schedule and countdown tracker. ${siteDescription}`;
  }

  // Generic site without sport info
  if (siteName && siteDescription === 'The Countdown Continues...') {
    return `${siteName} - Your ultimate sports countdown and game schedule tracker. Never miss a game with live countdowns, team schedules, and upcoming games.`;
  }

  // Fallback
  return siteDescription;
};

/**
 * Generate PWA manifest name
 * @param {Object} config - Configuration object
 * @param {string} config.siteName - Site name
 * @returns {string} Manifest name (e.g., "Lakers Time - The Countdown Continues...")
 */
export const generateManifestName = ({ siteName = '' }) => {
  if (!siteName) return 'The Countdown Continues...';
  return `${siteName} - The Countdown Continues...`;
};

/**
 * Get all metadata for a site based on configuration
 * This is the main function to use for generating all site metadata
 *
 * @param {Object} configuration - Site configuration from API
 * @returns {Object} Complete metadata object
 */
export const getSiteMetadata = (configuration) => {
  if (!configuration) {
    return {
      siteName: '',
      siteTitle: '',
      siteDescription: 'The Countdown Continues...',
      enhancedDescription: 'The Countdown Continues...',
      keywords:
        'sports events countdown, events schedule, all sports leagues, all teams, all sports, game information',
      manifestName: 'The Countdown Continues...',
      sportType: '',
      sportLevel: '',
      leagueName: '',
      teamName: '',
      isOneTeam: false,
      isOneLeague: false,
      actionWord: 'start',
    };
  }

  const siteName = configuration.site_name || '';
  const siteDescription =
    configuration.site_description || 'The Countdown Continues...';
  const sportType = configuration.sport_type || '';
  const sportLevel = configuration.sport_level || '';
  const origin = window.location.origin;

  // Determine if this is a one-team or one-league site
  const isOneTeam =
    configuration.display_one_team === '1' ||
    configuration.display_one_team === 1;
  const isOneLeague =
    configuration.display_one_league === '1' ||
    configuration.display_one_league === 1;

  // Get team/league names
  const teamName = configuration.one_team_name || '';
  const leagueName = configuration.one_league_name || '';

  // Get sport-specific action word
  const actionWord = getSportActionWord(sportType, leagueName);

  // Generate all metadata
  const siteTitle = generateSiteTitle({ siteName, origin });
  const keywords = generateKeywords({
    sportType,
    leagueName,
    teamName,
    isOneTeam,
    isOneLeague,
  });
  const enhancedDescription = generateEnhancedDescription({
    siteName,
    siteDescription,
    sportType,
    sportLevel,
    teamName,
    leagueName,
    isOneTeam,
    isOneLeague,
  });
  const manifestName = generateManifestName({ siteName });

  return {
    siteName,
    siteTitle,
    siteDescription,
    enhancedDescription,
    keywords,
    manifestName,
    sportType,
    sportLevel,
    leagueName,
    teamName,
    isOneTeam,
    isOneLeague,
    actionWord,
    origin,
  };
};
